/*
The admin's one way to read an email as HTML — and it is not a copy.

    const { cellSpans, findEditableCells, visibleText } = require('./emailCellFinder');

WHY THIS FILE IS FOUR LINES OF CODE AND FORTY OF PROSE.

There is already exactly one scanner for these documents: `ps-back/util/email/`
`htmlScan.js` and `editableCells.js`. It is comment-aware (this corpus is full of
MSO conditionals, and three separate comment-blindness bugs were found in
splitTemplate.js before it existed), it skips over quoted attribute values so a
`>` inside one cannot end a tag early, and it refuses unbalanced markup rather
than repairing it. Every one of those rules was paid for by a bug.

The block converter needs the same reading of the same bytes. It had three ways
to get it:

  1. Reimplement the rules here. That is the third copy the conversion probe
     explicitly warned about — "they must live in ONE shared module or the probe
     and the converter will disagree about what a button is". Rejected.
  2. Ask the server for the cells over GraphQL. getEmailTemplateCells already
     does that for the cell editor. But conversion has to run BEFORE anything is
     written and be re-runnable while the user compares side by side, and it
     needs the cells findEditableCells deliberately withholds (image-only cells,
     rule cells). A new resolver for that is a second place to keep in step.
  3. Require the module itself. This.

The dependency goes admin -> ps-back, which is the direction the repo already
runs in reverse: ps-back/scripts/emails/blockConversionProbe.js requires
ps-admin-template-store/src/utils/emailBlocks.js by relative path for exactly the
same reason — one definition, no drift.

This is NOT the pattern the rest of this folder uses, and the difference is the
point. `emailDraft.js` says "Ported from ps-back/util/email/extractVariables.js"
and `emailSampleRender.js` says "Ported from ps-back/util/email/normalizeHtml.js"
— both are copies, both are small, and both are already places where a fix on one
side has to be remembered on the other. `editableCells.js` is neither small nor
finished, and the probe had already been bitten once by exactly this, so it is
required rather than retyped.

IS IT SAFE TO BUNDLE? Yes, and it was checked rather than assumed.
`editableCells.js` and `htmlScan.js` are pure CommonJS over strings: no `fs`, no
`path`, no `process`, no mongoose, nothing from ps-back's runtime. webpack
resolves the relative path out of the package like any other, and babel-loader
transpiles it because the path holds no `node_modules` segment (see the exclude
in webpack.common.js). On the server side `webpack-node-externals` externalises
`node_modules` names only, so a relative request like this one is inlined too —
verified by building `server.js` and finding `cellSpans` in it. The admin ships
as those two bundles and nothing else (the Dockerfile copies `server.js` and
`public/`), so the require is resolved at build time and never at runtime.

WHAT WOULD BREAK IT. Anything added to `editableCells.js` or `htmlScan.js` that
touches node builtins or ps-back's Mongo layer. That is what this seam is for:
the whole cross-package import is on one line, so the day that happens there is
one file to fix and one comment explaining what it was doing.
*/

// eslint-disable-next-line import/no-unresolved, import/extensions
const editableCells = require('../../../ps-back/util/email/editableCells');
// eslint-disable-next-line import/no-unresolved, import/extensions
const htmlScan = require('../../../ps-back/util/email/htmlScan');

module.exports = {
  // Every cell span in the document, innermost-first per nesting level, over
  // the cell's INNER content. Includes the cells findEditableCells rejects.
  cellSpans: editableCells.cellSpans,
  // The subset of those that hold human copy and can be handed to an editor.
  findEditableCells: editableCells.findEditableCells,
  // What fraction of a document's visible text those cells account for.
  cellCoverage: editableCells.cellCoverage,
  // The finder's own definition of visible text. Anything that measures text
  // against a cell must measure it with this, or the two disagree.
  visibleText: editableCells.visibleText,
  // Comment-aware matching. The reason a <td> written inside an MSO conditional
  // is never mistaken for a real cell.
  scanOutsideComments: htmlScan.scanOutsideComments,
  isTagBalanced: htmlScan.isTagBalanced,
  VOID_ELEMENTS: htmlScan.VOID_ELEMENTS,
};
